import {useEffect, useRef, useState} from "react";
import {InputError} from "../FormControl/Input";
import "./timpicker.css";

export const TimePicker = ({ value, onChange, error, name, required = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedTime, setSelectedTime] = useState('');
    const timePickerRef = useRef(null);

    const generateTimeSlots = () => {
        const slots = [];
        for (let hour = 8; hour <= 20; hour++) {
            for (let minute = 0; minute < 60; minute += 10) {
                const period = hour >= 12 ? 'PM' : 'AM';
                const displayHour = hour % 12 || 12;
                const timeString = `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
                const valueString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                slots.push({
                    display: timeString,
                    value: valueString
                });
            }
        }
        return slots;
    };

    const timeSlots = generateTimeSlots();

    useEffect(() => {
        if (value) {
            // Handle different time formats that might come in
            let timePart = value;

            // If it's a full ISO date string (e.g., "2023-05-15T14:30:00Z")
            if (value.includes('T')) {
                timePart = value.split('T')[1].substring(0, 5);
            }
            // If it's just time with seconds/milliseconds (e.g., "14:30:00.000Z")
            else if (value.includes('Z')) {
                timePart = value.split('Z')[0].substring(0, 5);
            }
            // If it's already in HH:mm format
            else if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value)) {
                timePart = value;
            }

            // Validate the time format
            if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timePart)) {
                setSelectedTime(timePart);
            } else {
                // Default to first time slot if format is invalid
                setSelectedTime(timeSlots[0]?.value || '08:00');
            }
        } else {
            // Default to first time slot if no value provided
            setSelectedTime(timeSlots[0]?.value || '08:00');
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (timePickerRef.current && !timePickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleTimeSelect = (timeValue) => {
        setSelectedTime(timeValue);
        onChange(timeValue); // Just send HH:mm format
        setIsOpen(false);
    };

    const toggleTimePicker = () => {
        setIsOpen(!isOpen);
    };

    const getDisplayTime = () => {
        if (!selectedTime) return 'Select time';
        const [hours, minutes] = selectedTime.split(':');
        const hour = parseInt(hours);
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${period}`;
    };

    return (
        <div className="time-picker" ref={timePickerRef}>
            <div
                className="time-picker-input"
                onClick={toggleTimePicker}
                tabIndex={0}
            >
                {getDisplayTime()}
                <span className="time-picker-arrow">▼</span>
            </div>

            {isOpen && (
                <div className="time-slot-container">
                    {timeSlots.map((slot) => (
                        <div
                            key={slot.value}
                            className={`time-slot ${selectedTime === slot.value ? 'selected' : ''}`}
                            onClick={() => handleTimeSelect(slot.value)}
                        >
                            {slot.display}
                        </div>
                    ))}
                </div>
            )}

            {error && <InputError message={error.message} />}
            <input
                type="hidden"
                name={name}
                value={selectedTime}
                required={required}
            />
        </div>
    );
};